import React, { useCallback } from 'react'
import { fromInputDate, toInputDate } from '../utils/dateNormalization'
import { farmDateTimeInputValue, farmWallClockToInstant } from '../utils/farmTime'

type Props = Readonly<{
  id?: string
  className?: string
  disabled?: boolean
  value: string
  onChangeValue: (nextValue: string) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  invalid?: boolean
  pickerOnly?: boolean
  placeholder?: string
  style?: React.CSSProperties
  onRequestReveal?: (input: HTMLInputElement) => void
  /**
   * Findings #85: show and edit the value in this time zone (the farm's).
   * Without it the browser's zone is used, as before.
   */
  timeZone?: string
}>

const isPrintableKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
  // `key` is a single character for printable keys.
  // We also treat Backspace/Delete as manual-edit keys.
  if (e.key.length === 1) return true
  if (e.key === 'Backspace' || e.key === 'Delete') return true
  return false
}

const isAllowedControlKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
  // Let users navigate/focus without editing.
  const allowed = new Set([
    'Tab',
    'Enter',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End'
  ])

  if (allowed.has(e.key)) return true

  // Allow common OS/browser shortcuts (copy/select-all/etc.)
  if (e.ctrlKey || e.metaKey) return true

  return false
}

export default function DateTimeLocalInput({
  id,
  className,
  disabled,
  value,
  onChangeValue,
  onBlur,
  invalid,
  pickerOnly = true,
  placeholder,
  style,
  onRequestReveal,
  timeZone
}: Props) {
  const tryOpenPicker = useCallback((input: HTMLInputElement) => {
    if (!pickerOnly) return
    const anyInput = input as any
    if (typeof anyInput.showPicker === 'function') {
      try {
        anyInput.showPicker()
      } catch {
        // ignore
      }
    }
  }, [pickerOnly])

  return (
    <input
      id={id}
      type="datetime-local"
      className={className}
      disabled={disabled}
      aria-invalid={invalid ? true : undefined}
      value={timeZone ? farmDateTimeInputValue(value, timeZone) : toInputDate(value)}
      inputMode={pickerOnly ? 'none' : undefined}
      onPointerDown={(e) => {
        onRequestReveal?.(e.currentTarget)
        tryOpenPicker(e.currentTarget)
      }}
      onFocus={(e) => {
        onRequestReveal?.(e.currentTarget)
      }}
      onKeyDown={(e) => {
        if (!pickerOnly) return
        if (isAllowedControlKey(e)) return
        if (isPrintableKey(e)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onBeforeInput={(e) => {
        if (!pickerOnly) return
        // Block IME/virtual-keyboard edits, while still allowing the picker.
        e.preventDefault()
      }}
      onPaste={(e) => {
        if (!pickerOnly) return
        e.preventDefault()
      }}
      onChange={(e) => {
        const iso = timeZone
          ? farmWallClockToInstant(e.target.value, timeZone)
          : fromInputDate(e.target.value)
        onChangeValue(iso ?? e.target.value)
      }}
      onBlur={onBlur}
      style={style}
      placeholder={placeholder}
    />
  )
}
